import React, { useCallback, useEffect, useState } from 'react';

import PageHeader from '../../components/PageHeader.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { BusinessSchemaBackupAPI } from '../../lib/api';
import { notify } from '../../lib/notifications';

/**
 * Business → Backup.
 *
 * Download and restore the PostgreSQL schema for this organisation
 * only. The host selects the schema; the page never sends another
 * organisation's schema name.
 */
async function unwrapError(err) {
  const data = err?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    const text = await data.text();
    try {
      err.response.data = text ? JSON.parse(text) : { detail: 'Request failed.' };
    } catch {
      err.response.data = { detail: text.slice(0, 800) || 'Request failed.' };
    }
  }
  return err;
}

function downloadBlobResponse(response, fallbackName) {
  const blob = new Blob([response.data], {
    type: response.headers['content-type'] || 'application/zip',
  });
  let filename = fallbackName;
  const cd = response.headers['content-disposition'] || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  if (match && match[1]) filename = decodeURIComponent(match[1]);
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

export default function BusinessSchemaBackup() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [confirm, setConfirm] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setInfo(await BusinessSchemaBackupAPI.status());
    } catch (err) {
      setError(await unwrapError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const download = useCallback(async () => {
    setDownloading(true);
    setError(null);
    try {
      const response = await BusinessSchemaBackupAPI.download();
      const schema = (info && info.schema_name) || 'schema';
      downloadBlobResponse(response, `taskydo-${schema}.schema-backup.zip`);
      notify.success('Schema backup downloaded.');
    } catch (err) {
      setError(await unwrapError(err));
    } finally {
      setDownloading(false);
    }
  }, [info]);

  const restore = useCallback(async () => {
    if (!file || !info?.schema_name) return;
    setRestoring(true);
    setError(null);
    try {
      const result = await BusinessSchemaBackupAPI.restore(file, info.schema_name);
      setFile(null);
      setFileInputKey((key) => key + 1);
      setConfirm('');
      notify.success(
        `Restored ${result.rows_restored} rows in schema ${result.schema_name}.`,
      );
      await reload();
    } catch (err) {
      setError(await unwrapError(err));
    } finally {
      setRestoring(false);
    }
  }, [file, info, reload]);

  if (loading && !info) return <Loader label="Loading schema backup…" />;

  const available = !!(info && info.available);
  const schemaName = (info && info.schema_name) || '';
  const confirmMatches = available && confirm === schemaName;
  const tooLarge = !!(
    file
    && info
    && info.max_upload_bytes
    && file.size > info.max_upload_bytes
  );
  const busy = downloading || restoring;

  return (
    <>
      <PageHeader
        title="💾 Schema backup"
        subtitle="Download or restore this organisation's data. Other organisations stay in their own schemas and are not included."
      />

      <ErrorBox error={error} />

      <section className="ty-card" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>
          What is in the file
        </h2>
        <p style={{ color: 'var(--ty-muted)', marginTop: 0 }}>
          The database holds every organisation. This backup is limited to
          the schema for the organisation you are signed in to
          {schemaName ? (
            <>
              {' '}(<code>{schemaName}</code>)
            </>
          ) : null}
          . It does not download the rest of the database.
          {info?.organisation_name ? ` Organisation: ${info.organisation_name}.` : ''}
          {available ? ` ${info.table_count} table${info.table_count === 1 ? '' : 's'} included.` : ''}
        </p>
        {available && info.tables && info.tables.length ? (
          <details>
            <summary style={{ cursor: 'pointer' }}>Tables in this schema</summary>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: 'var(--ty-muted)' }}>
              {info.tables.map((table) => (
                <li key={table}><code>{table}</code></li>
              ))}
            </ul>
          </details>
        ) : null}
        {!available && info?.reason ? (
          <p style={{ marginBottom: 0 }}>{info.reason}</p>
        ) : null}
      </section>

      {available ? (
        <>
          <section className="ty-card" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>
              Download
            </h2>
            <p style={{ color: 'var(--ty-muted)', marginTop: 0 }}>
              The zip contains rows from schema <code>{schemaName}</code> only.
              Uploaded files stored outside the database (logos and
              attachments on disk) are not inside the zip. Migration history
              stays on the current application version.
            </p>
            <button
              type="button"
              className="ty-btn"
              onClick={download}
              disabled={busy}
            >
              {downloading ? 'Preparing backup…' : 'Download backup'}
            </button>
          </section>

          <section className="ty-card">
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>
              Restore
            </h2>
            <div
              style={{
                borderLeft: '4px solid var(--ty-warning)',
                background: '#fffbeb',
                padding: '12px 14px',
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              Restoring replaces the rows in schema <code>{schemaName}</code>{' '}
              with the file. Tables created after the backup are emptied.
              No other organisation schema is written.
            </div>

            <div className="ty-field">
              <label htmlFor="schema-backup-file">Backup file</label>
              <input
                id="schema-backup-file"
                key={fileInputKey}
                className="ty-input"
                type="file"
                accept=".zip,application/zip"
                onChange={(event) => {
                  const next = event.target.files && event.target.files[0];
                  setFile(next || null);
                }}
              />
            </div>
            {tooLarge ? (
              <p className="ty-error" role="alert">This file is larger than the restore limit.</p>
            ) : null}

            <div className="ty-field">
              <label htmlFor="schema-backup-confirm">
                Type <code>{schemaName}</code> to confirm
              </label>
              <input
                id="schema-backup-confirm"
                className="ty-input"
                value={confirm}
                autoComplete="off"
                onChange={(event) => setConfirm(event.target.value)}
                placeholder={schemaName}
              />
            </div>

            <button
              type="button"
              className="ty-btn ty-btn--danger"
              onClick={restore}
              disabled={busy || !file || !confirmMatches || tooLarge}
            >
              {restoring ? 'Restoring…' : 'Restore backup'}
            </button>
          </section>
        </>
      ) : null}
    </>
  );
}
